export type Nesting = -1 | 0 | 1;

export interface Token {
  type: string;
  tag: string;
  attrs: Array<[string, string]> | null;
  map: [number, number] | null;
  nesting: Nesting;
  level: number;
  children: Token[] | null;
  content: string;
  markup: string;
  info: string;
  meta: unknown;
  block: boolean;
  hidden: boolean;
  attrIndex: (name: string) => number;
  attrPush: (attrData: [string, string]) => void;
  attrSet: (name: string, value: string) => void;
  attrGet: (name: string) => string | null;
  attrJoin: (name: string, value: string) => void;
}

function createToken(type: string, tag: string, nesting: number): Token {
  const token: Partial<Token> = {} as Token;

  token.type = type;
  token.tag = tag;
  token.attrs = null;
  token.map = null;
  token.nesting = nesting as Nesting;
  token.level = 0;
  token.children = null;
  token.content = '';
  token.markup = '';
  token.info = '';
  token.meta = null;
  token.block = false;
  token.hidden = false;

  token.attrIndex = function attrIndex(name: string): number {
    if (!this.attrs) {
      return -1;
    }
    const attrs = this.attrs;
    for (let i = 0, len = attrs.length; i < len; i++) {
      if (attrs[i][0] === name) {
        return i;
      }
    }
    return -1;
  };

  token.attrPush = function attrPush(attrData: [string, string]) {
    if (this.attrs) {
      this.attrs.push(attrData);
    } else {
      this.attrs = [attrData];
    }
  };

  token.attrSet = function attrSet(name: string, value: string) {
    const idx = this.attrIndex(name);
    const attrData: [string, string] = [name, value];
    if (idx < 0) {
      this.attrPush(attrData);
    } else {
      this.attrs![idx] = attrData;
    }
  };

  token.attrGet = function attrGet(name: string): string | null {
    const idx = this.attrIndex(name);
    let value = null;
    if (idx >= 0) {
      value = this.attrs![idx][1];
    }
    return value;
  };

  token.attrJoin = function attrJoin(name: string, value: string) {
    const idx = this.attrIndex(name);
    if (idx < 0) {
      this.attrPush([name, value]);
    } else {
      this.attrs![idx][1] = `${this.attrs![idx][1]} ${value}`;
    }
  };

  return token as Token;
}

export default createToken as unknown as {
  new (type: string, tag: string, nesting: number): Token;
};
